'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';


import Form, {
    IChangeEvent
} from '@rjsf/core';
import ErrorList from '@rjsf/core/lib/components/templates/ErrorList';
import {
    createSchemaUtils,
    englishStringTranslator,
    ErrorSchema,
    RJSFSchema,
    RJSFValidationError
} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';


import {
    InputTextarea,
    InputTextareaProps
} from 'primereact/inputtextarea';
import {Toolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {
    LoadConfigParameters as CalendarLoadConfigParameters,
    LoadConfigResult as CalendarLoadConfigResult
} from '~/shared/src/utilities/modules/calendar';
import {
    LoadConfigParameters as OverviewLoadConfigParameters,
    LoadConfigResult as OverviewLoadConfigResult
} from '~/shared/src/utilities/modules/overview';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {
    CursorAnchor,
    getAnchor,
    resolveAnchor
} from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField/cursorAnchor';
import FiltersField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField/SpecialFields/FiltersField';
import SortersField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField/SpecialFields/SortersField';

import xhr from '~/frontend/src/utilities/xhr';
import {StoreState} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {Variables} from '~/frontend/src/components/base/Form';
import parse from 'html-react-parser';
import {isInvalidHTML, stripHTMLtags} from '~/frontend/src/utilities/valueRenderers';
import DefaultFieldTemplate from '@rjsf/core/lib/components/templates/FieldTemplate';
import DefaultCheckboxWidget from '@rjsf/core/lib/components/widgets/CheckboxWidget';
import DefaultSelectWidget from '@rjsf/core/lib/components/widgets/SelectWidget';

/**
 * rjsf renders schema `title`/`description` as plain text. Ours come from the message catalog
 * (every schema string is run through `__()`), so they may carry markup - e.g. the original
 * Intl option name appended in `yn-secondary`. Parse it like every other message-sourced markup
 * in the app (`html-react-parser`, same as StaticContentField) instead of injecting raw HTML.
 */
const renderMarkup = ( value : any ) => {
    if ( !value || typeof value !== 'string' ) {
        return value;
    }

    return !isInvalidHTML( value ) ? parse( value ) : value;
};

const HtmlTitleField = ( { id, title, required } : any ) => title
    ? <label className="control-label" id={id}>
        {renderMarkup( title )}
        {required && <span className="required">*</span>}
    </label>
    : null;

const HtmlDescriptionField = ( { id, description } : any ) => description
    ? <div className="field-description" id={id}>{renderMarkup( description )}</div>
    : null;

/**
 * Section headings go through `TitleFieldTemplate`, but the label of a single property is
 * rendered by `FieldTemplate` - so that one needs the same treatment. rjsf's default `Label`
 * renders the label as a child, so a parsed node can be passed straight through.
 */
const HtmlFieldTemplate = ( props : any ) =>
    <DefaultFieldTemplate {...props} label={renderMarkup( props.label )}/>;

/** Booleans render as a checkbox, whose label the widget draws itself - so it needs the same treatment. */
const HtmlCheckboxWidget = ( props : any ) =>
    <DefaultCheckboxWidget {...props} label={renderMarkup( props.label )}/>;

/**
 * The anyOf/oneOf switcher lists the sub-schema titles as `<option>` text, which cannot render
 * markup at all. Strip the tags there so a styled title does not leak as raw HTML.
 */
/**
 * ajv builds messages like `must have required property '<title>'` - the title is embedded as raw
 * text, so any markup in it would leak into the error. Strip the tags there.
 */
const stripErrorMarkup = ( errors : any[] ) => errors.map(
    ( error : any ) => ( {
        ...error,
        message: typeof error.message === 'string' ? stripHTMLtags( error.message ) : error.message,
        stack  : typeof error.stack === 'string' ? stripHTMLtags( error.stack ) : error.stack
    } )
);

const HtmlSelectWidget = ( props : any ) => {
    const enumOptions = props.options?.enumOptions?.map(
        ( option : any ) => typeof option.label === 'string'
            ? { ...option, label: stripHTMLtags( option.label ) }
            : option
    );

    return <DefaultSelectWidget {...props} options={{ ...props.options, enumOptions }}/>;
};



type InputProps = Omit<InputTextareaProps, 'autoComplete' | 'onChange' | 'key'>
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLTextAreaElement>;

type SpecialFieldData = {
    dataStructureForField : DataStructure;
    columnDefinitions : OverviewLoadConfigResult['data']['columnDefinitions'];
    defaultFilters? : OverviewLoadConfigResult['data']['defaultFilters'];
    defaultFixFilters? : OverviewLoadConfigResult['data']['fixFilters'];
    defaultSorters? : OverviewLoadConfigResult['data']['defaultSorters'];
};

type SpecialFieldConfig = {
    viewType : 'calendar' | 'calendarView' | 'chart' | 'chartView' | 'overview' | 'overviewView';
    viewFieldId : number;
    viewElementId : number;
    viewKey : string;
    type : 'columns' | 'filters' | 'fixfilters' | 'sorters';
    dataStructureForValue? : DataStructure;
};


type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The number of text rows to be displayed within the field.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    disabled       : false,
    height         : 3,
    maxHeight      : 0,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    id? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    jsonSchema? : RJSFSchema;
    uiSchema? : { [ key : string ] : any };

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    onInvalid? : ( errors : string[] ) => void;
    onIntermediateInvalid? : ( errors : string[] ) => void;

    onValid? : () => void;
    onIntermediateValid? : () => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };

    variables? : Variables;
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


const JsonField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const {
                  uid,
                  //tabIndex,
                  disabled,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  onIntermediateInvalid,
                  onIntermediateValid,
                  onInvalid,
                  onValid,
                  originalValue,
                  placeholder,
                  pt,
                  tabIndex,
                  uiSchema,
                  useHookForm,
                  value,
                  variables
              }                                             = props,
              user                                          = useSelector( ( state : StoreState ) => state.session?.user ),
              elementId                                     = id ?? `${uid}_jsonField`,
              inputRef                                      = useRef<HTMLTextAreaElement>( null ),
              inValue                                       = useRef<string | undefined>( value ),
              outValue                                      = useRef<string | undefined>( value ),
              timeoutId                                     = useRef<NodeJS.Timeout | undefined>(),
              validTimeoutId                                = useRef<NodeJS.Timeout | undefined>(),
              specialFieldData                              = useRef<SpecialFieldData | undefined>(),
              pendingCursorAnchor                           = useRef<{ anchor : CursorAnchor | null; fallbackOffset : number; domValueBeforeUpdate : string } | null>( null ),
              [ elementValue, setElementValue ]             = useState<string | undefined>( value ),
              [ editorMode, setEditorMode ]                 = useState<'editor' | 'text'>( 'editor' ),
              [ focus, setFocus ]                           = useState<boolean>( false ),
              [ errors, setErrors ]                         = useState<RJSFValidationError[]>( [] ),
              [ errorSchema, setErrorSchema ]               = useState<ErrorSchema | undefined>( undefined ),
              specialFieldConfig                            = useMemo(
                  () => {
                      let retVal : SpecialFieldConfig | undefined;

                      if ( variables?.formDataContext && variables?.formRootItemId && variables?.propertyTypeName ) {
                          const formRootItem = global.data[ variables.formDataContext ]?.items[ variables.formRootItemId ];

                          switch ( formRootItem?.itemType.name ) {
                              case 'CalendarView':
                                  retVal = {
                                      viewType             : 'calendarView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Calendar' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;

                              case 'Chart':
                                  retVal = {
                                      viewType             : 'chart',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : global.data[ variables.formDataContext ]?.items[ variables.formRootItemId ]?.key ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };

                                  specialFieldData.current = {
                                      dataStructureForField: variables.dataStructure,
                                      columnDefinitions    : variables.columnDefinitions
                                  };
                                  break;

                              case 'ChartView':
                                  retVal = {
                                      viewType             : 'chartView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Chart' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;

                              case 'OverviewView':
                                  retVal = {
                                      viewType             : 'overviewView',
                                      viewFieldId          : variables.fieldItemId,
                                      viewElementId        : variables.formRootItemId,
                                      viewKey              : dataExtraction.extractReferencedItemKey(
                                          formRootItem,
                                          variables.formDataContext,
                                          [
                                              { direction: 'DestToSrc', referenceType: 'belongsTo', targetItemType: 'Overview' }
                                          ]
                                      ) ?? '',
                                      type                 : variables.propertyTypeName.toLowerCase(),
                                      dataStructureForValue: variables.dataStructure
                                  };
                                  break;
                          }
                      }

                      return retVal;
                  },
                  [
                      variables?.dataStructure,
                      variables?.formDataContext,
                      variables?.formRootItemId,
                      variables?.propertyTypeName
                  ]
              ),
              jsonSchema                                    = useMemo(
                  () => {
                      let retVal = undefined;

                      if ( props.jsonSchema ) {
                          retVal = props.jsonSchema;
                      }

                      // convert enum: [ { code: <code>, description: '<description>' }, ... ]
                      //  to enumNames: [ '<description>', ... ]
                      //  and enum: [ <code>, ... ]
                      // resolve messages in strings
                      if ( retVal ) {
                          const recursiveProcessEnum = ( part : any ) => {
                              if ( typeof part === 'object' ) {
                                  const partKeys = Object.keys( part );

                                  for ( const partKey of partKeys ) {
                                      if ( partKey === 'enum' ) {
                                          if ( Array.isArray( part.enum ) ) {
                                              const enumNames : string[] = [],
                                                    newEnum : string[]   = [];

                                              let converted = false;

                                              for ( let i = 0; i < part.enum.length; i++ ) {
                                                  if ( typeof part.enum[ i ] === 'object' ) {
                                                      converted = true;

                                                      enumNames.push( __( part.enum[ i ].description ) ?? part.enum[ i ].value ?? part.enum[ i ].code );
                                                      newEnum.push( part.enum[ i ].value ?? part.enum[ i ].code );
                                                  }
                                                  else {
                                                      enumNames.push( part.enum[ i ] );
                                                      newEnum.push( part.enum[ i ] );
                                                  }
                                              }

                                              if ( converted ) {
                                                  part.enumNames = enumNames;
                                                  part.enum = newEnum;
                                              }
                                          }
                                      }
                                      else if ( typeof part[ partKey ] === 'string' ) {
                                          part[ partKey ] = __( part[ partKey ] );
                                      }
                                      else {
                                          recursiveProcessEnum( part[ partKey ] );
                                      }
                                  }
                              }
                          };

                          recursiveProcessEnum( retVal );
                      }

                      return retVal;
                  },
                  [
                      props.jsonSchema
                  ]
              ),
              heightStyle                                   = useMemo(
                  () => getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout ),
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                               = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                           = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              validateValue                                 = useCallback(
                  ( newValue : string ) => {
                      let retVal : RJSFValidationError[] = [],
                          newErrorSchema;

                      // validate against jsonSchema if
                      if ( jsonSchema ) {
                          try {
                              const parsedNewValue  = JSON.parse( newValue ),
                                    schemaUtils     = createSchemaUtils( validator, jsonSchema ),
                                    retrievedSchema = schemaUtils.retrieveSchema( jsonSchema, parsedNewValue );

                              ( { errors: retVal, errorSchema: newErrorSchema } = schemaUtils.getValidator().validateFormData( parsedNewValue, retrievedSchema ) );

                              if ( retVal.length ) {
                                  setErrors( retVal );
                                  setErrorSchema( newErrorSchema );
                              }
                              else {
                                  setErrors( [] );
                                  setErrorSchema( undefined );
                              }
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      return retVal;
                  },
                  [
                      jsonSchema
                  ]
              ),
              onIntermediateError                           = useCallback(
                  ( newErrors : RJSFValidationError[] ) => {
                      if ( onIntermediateInvalid && newErrors.length ) {
                          onIntermediateInvalid( newErrors.map( e => e.stack ) );
                      }
                  },
                  [
                      onIntermediateInvalid
                  ]
              ),
              onError                                       = useCallback(
                  ( newErrors : RJSFValidationError[] ) => {
                      if ( onInvalid && newErrors.length ) {
                          onInvalid( newErrors.map( e => e.stack ) );
                      }
                  },
                  [
                      onInvalid
                  ]
              ),
              onEditorChange                                = useCallback(
                  ( event : IChangeEvent ) => {
                      let changedValue = event.formData;

                      event = {
                          ...event,
                          currentTarget: document.getElementById( elementId )
                      } as IChangeEvent & { currentTarget : HTMLElement };

                      try {
                          changedValue = JSON.stringify( changedValue );
                      }
                      catch ( e ) {
                          changedValue = changedValue.replace( /\u00a0/gim, ' ' );
                      }

                      setErrors( [] );
                      setErrorSchema( undefined );

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );
                          pendingCursorAnchor.current = null;

                          if ( useHookForm ) {
                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                          else {
                              onChange( changedValue, event );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onEditorError                                 = useCallback(
                  ( editorErrors : RJSFValidationError[] ) => {
                      setErrors( editorErrors );
                      setErrorSchema( undefined );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              normaliseInput                                = useCallback(
                  ( rawValue : string ) : string => {
                      try {
                          const parsedChangedValue = JSON.parse( rawValue );

                          if ( elementValue && jsonSchema !== undefined && uiSchema !== undefined ) {
                              const parsedElementValue     = JSON.parse( elementValue ),
                                    parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                              for ( const pEVKey of parsedElementValueKeys ) {
                                  if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                      parsedChangedValue[ pEVKey ] = parsedElementValue[ pEVKey ];
                                  }
                              }
                          }

                          return JSON.stringify( parsedChangedValue );
                      }
                      catch ( e ) {
                          return rawValue;
                      }
                  },
                  [
                      elementValue,
                      jsonSchema,
                      uiSchema
                  ]
              ),
              commitChangedValue                            = useCallback(
                  ( changedValue : string, event : InputChangeProps | React.KeyboardEvent<HTMLTextAreaElement> ) => {
                      if ( changedValue === outValue.current ) return;

                      const newErrors = validateValue( changedValue );
                      setElementValue( changedValue );

                      if ( newErrors.length ) return;

                      if ( useHookForm ) {
                          onIntermediateChange?.( changedValue, event );
                          if ( timeoutId.current ) clearTimeout( timeoutId.current );
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                      else {
                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      useHookForm,
                      validateValue
                  ]
              ),
              onInputChange                                 = useCallback(
                  ( event : InputChangeProps ) => {
                      const domValue       = event.target.value,
                            rawValue       = domValue.replace( /\u00a0/gim, ' ' ),
                            selectionStart = event.target?.selectionStart ?? rawValue.length;

                      pendingCursorAnchor.current = {
                          anchor              : getAnchor( rawValue, selectionStart ),
                          fallbackOffset      : selectionStart,
                          domValueBeforeUpdate: domValue
                      };

                      commitChangedValue( normaliseInput( rawValue ), event );
                  },
                  [
                      commitChangedValue,
                      normaliseInput
                  ]
              ),
              onInputFocus                                  = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                                   = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( false );

                      setElementValue( outValue.current );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( !errors.length && outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      errors,
                      onBlur,
                      onChange
                  ]
              ),
              onInputTextareaKey                            = useCallback(
                  ( event : React.KeyboardEvent<HTMLTextAreaElement> ) => {
                      // Insert a literal newline on Enter and prevent any parent form submission.
                      if ( event.type === 'keydown' && event.key === 'Enter' ) {
                          const textarea = inputRef.current as unknown as HTMLTextAreaElement | null;
                          if ( !textarea ) return;

                          const lineBreak      = '\n',
                                currentValue   = textarea.value,
                                selectionStart = textarea.selectionStart,
                                selectionEnd   = textarea.selectionEnd,
                                rawValue       = currentValue.slice( 0, selectionStart ) + lineBreak + currentValue.slice( selectionEnd ),
                                cursorAfter    = selectionStart + lineBreak.length;

                          pendingCursorAnchor.current = {
                              anchor              : getAnchor( rawValue, cursorAfter ),
                              fallbackOffset      : cursorAfter,
                              // Enter is intercepted before the browser inserts the newline,
                              // so the current DOM value already represents the "before" state.
                              // Mark it as different from rawValue so useLayoutEffect always runs.
                              domValueBeforeUpdate: currentValue
                          };

                          commitChangedValue( normaliseInput( rawValue ), event );

                          event.preventDefault();
                          event.stopPropagation();
                      }
                  },
                  [
                      commitChangedValue,
                      normaliseInput
                  ]
              ),
              getEditor                                     = () : React.ReactElement => {
                  let editor : React.ReactElement | undefined,
                      editorValue = '';

                  if ( specialFieldConfig ) {
                      let originalEditorValue = '';

                      if ( elementValue ) {
                          try {
                              const parsedElementValue = JSON.parse( elementValue );

                              if ( uiSchema !== undefined ) {
                                  const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                  for ( const pEVKey of parsedElementValueKeys ) {
                                      if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                          delete parsedElementValue[ pEVKey ];
                                      }
                                  }
                              }

                              editorValue = parsedElementValue;
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      if ( originalValue ) {
                          try {
                              const parsedElementValue = JSON.parse( originalValue );

                              if ( uiSchema !== undefined ) {
                                  const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                  for ( const pEVKey of parsedElementValueKeys ) {
                                      if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                          delete parsedElementValue[ pEVKey ];
                                      }
                                  }
                              }

                              originalEditorValue = parsedElementValue;
                          }
                          catch ( e ) {
                              // do nothing
                          }
                      }

                      switch ( specialFieldConfig.type ) {
                          case 'columns':
                              break;

                          case 'filters':
                          case 'fixfilters':
                              editor = <FiltersField
                                  uid={elementId}
                                  columnDefinitions={specialFieldData.current?.columnDefinitions ?? []}
                                  dataStructureForField={specialFieldData.current?.dataStructureForField ?? { origId: 0 } as DataStructure}
                                  dataStructureForValue={specialFieldConfig.dataStructureForValue}
                                  defaultFilters={specialFieldConfig.type !== 'fixfilters' ? specialFieldData.current?.defaultFilters ?? [] : specialFieldData.current?.defaultFixFilters ?? []}
                                  value={editorValue ? editorValue as unknown as ColumnFilter[] : undefined}
                                  originalValue={originalEditorValue ? originalEditorValue as unknown as ColumnFilter[] : undefined}
                                  onChange={( changedValue : ColumnFilter[] | undefined ) => {
                                      onInputChange( {
                                          target       : {
                                              value: changedValue
                                                  ? JSON.stringify( changedValue )
                                                  : ''
                                          },
                                          currentTarget: document.getElementById( elementId )
                                      } as InputChangeProps );
                                  }}
                                  markDirty={markDirty}
                                  disabled={disabled}
                                  tabIndex={tabIndex}
                              />;
                              break;

                          case 'sorters':
                              editor = <SortersField
                                  uid={elementId}
                                  columnDefinitions={specialFieldData.current?.columnDefinitions ?? []}
                                  defaultSorters={specialFieldData.current?.defaultSorters ?? []}
                                  value={editorValue ? editorValue as unknown as ColumnSorter[] : undefined}
                                  originalValue={originalEditorValue ? originalEditorValue as unknown as ColumnSorter[] : undefined}
                                  onChange={( changedValue : ColumnSorter[] | undefined ) => {
                                      onInputChange( {
                                          target       : {
                                              value: changedValue
                                                  ? JSON.stringify( changedValue )
                                                  : ''
                                          },
                                          currentTarget: document.getElementById( elementId )
                                      } as InputChangeProps );
                                  }}
                                  markDirty={markDirty}
                                  disabled={disabled}
                                  tabIndex={tabIndex}
                              />;
                              break;
                      }

                      if ( editor ) {
                          editor = <div
                              id={uid}
                              className={`yn-jsonfield-wrapper${!height ? ' yn-auto-height' : ''}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                          >
                              {editor}
                          </div>
                      }
                  }

                  if ( !editor ) {
                      if ( jsonSchema !== undefined && editorMode !== 'text' ) {
                          if ( elementValue ) {
                              try {
                                  editorValue = JSON.parse( elementValue );
                              }
                              catch ( e ) {
                                  // do nothing
                              }
                          }

                          editor = <Form
                              id={elementId}

                              formData={elementValue ? editorValue : {}}
                              onChange={onEditorChange}
                              onError={onEditorError}

                              schema={jsonSchema}
                              uiSchema={{
                                  'ui:rootFieldId'            : 'editor-root',
                                  'ui:fieldReplacesAnyOrOneOf': true,
                                  'ui:submitButtonOptions'    : {
                                      norender: true
                                  },
                                  ...uiSchema
                              }}
                              validator={validator}

                              transformErrors={stripErrorMarkup}

                              widgets={{
                                  CheckboxWidget: HtmlCheckboxWidget,
                                  SelectWidget  : HtmlSelectWidget
                              }}

                              templates={{
                                  FieldTemplate           : HtmlFieldTemplate,
                                  TitleFieldTemplate      : HtmlTitleField,
                                  DescriptionFieldTemplate: HtmlDescriptionField
                              }}

                              showErrorList={infoTextLayout === 'text' ? 'top' : false}
                              liveValidate={true}
                              liveOmit={true}
                              omitExtraData={true}
                              disabled={disabled}
                              readonly={disabled}
                          />;
                      }
                      else {
                          if ( elementValue ) {
                              try {
                                  const parsedElementValue = JSON.parse( elementValue );

                                  if ( uiSchema !== undefined ) {
                                      const parsedElementValueKeys = Object.keys( parsedElementValue ) as string[];

                                      for ( const pEVKey of parsedElementValueKeys ) {
                                          if ( uiSchema[ pEVKey ]?.[ 'ui:style' ]?.display === 'none' ) {
                                              delete parsedElementValue[ pEVKey ];
                                          }
                                      }
                                  }

                                  editorValue = JSON.stringify( parsedElementValue, null, '\u00a0\u00a0\u00a0\u00a0' );
                              }
                              catch ( e ) {
                                  // preserve given format
                                  editorValue = elementValue.replace( / /gim, '\u00a0' );
                              }
                          }

                          editor = <InputTextarea
                              {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                              id={elementId}

                              ref={inputRef}

                              value={editorValue ?? ''}

                              onChange={onInputChange}

                              pt={{
                                  root: {
                                      onFocus  : onInputFocus,
                                      onBlur   : onInputBlur,
                                      onKeyUp  : onInputTextareaKey,
                                      onKeyDown: onInputTextareaKey
                                  }
                              }}

                              className={`yn-jsonfield yn-jsonfield-plaintext ${inputClassNames.join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}

                              placeholder={resolvedPlaceholder}
                              tooltip={undefined}
                              tooltipOptions={undefined}

                              aria-describedby={`${elementId}_message`}
                          />;

                          if ( infoTextLayout === 'text' && errors.length ) {
                              editor = <>
                                  <div className="yn-jsonfield-error-wrapper">
                                      <ErrorList
                                          errors={errors}
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          errorSchema={errorSchema}
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-ignore
                                          registry={{
                                              translateString: englishStringTranslator
                                          }}
                                      />
                                  </div>
                                  {editor}
                              </>
                          }
                      }
                  }

                  if ( jsonSchema !== undefined ) {
                      const layoutComponents : ToolbarLayoutComponents = {
                          left : [],
                          right: []
                      };

                      layoutComponents.left.push( {
                          type  : 'buttonSet',
                          params: {
                              key: `${uid}_panelButtons`,

                              buttons: [
                                  {
                                      type  : 'button',
                                      params: {
                                          key: `${uid}_editorButton`,

                                          label    : __( 'YN.base.jsonField.editorMode.editor.label' ),
                                          onClick  : () => {
                                              setEditorMode( 'editor' )
                                          },
                                          className: editorMode === 'editor' ? '' : 'p-button-outlined'
                                      }
                                  },
                                  {
                                      type  : 'button',
                                      params: {
                                          key: `${uid}_textButton`,

                                          label    : __( 'YN.base.jsonField.editorMode.text.label' ),
                                          onClick  : () => {
                                              setEditorMode( 'text' )
                                          },
                                          className: editorMode === 'text' ? '' : 'p-button-outlined'
                                      }
                                  }
                              ]
                          }
                      } );

                      const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

                      editor = <div
                          className={`yn-jsonfield yn-jsonfield-editor ${inputClassNames.join( ' ' )} ${disabled ? 'yn-disabled' : ''} ${focus ? 'p-focus' : ''}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                      >
                          <Toolbar
                              start={toolbarComponents.left}
                              end={toolbarComponents.right}
                              className="yn-panel-toolbar yn-toolbar-horizontal-separator-bottom"
                              style={{ zIndex: 6 }}
                          />
                          {editor}
                      </div>;
                  }

                  return editor;
              };

        inValue.current = value;
        outValue.current = elementValue;

        // load special field data
        useEffect(
            () => {
                if ( specialFieldConfig ) {
                    let loadFnc : ( () => Promise<void> ) | undefined;

                    switch ( specialFieldConfig.viewType ) {
                        case 'calendar':
                            break;

                        case 'calendarView':
                            loadFnc = async () => {
                                const response = await xhr.post(
                                    `${BASE_URL}/api/calendar/loadConfig`,
                                    {
                                        viewKey: specialFieldConfig.viewKey
                                    } as CalendarLoadConfigParameters,
                                    undefined,
                                    uid,
                                    'loadSpecialFieldData'
                                );

                                if ( response?.data ) {
                                    const config = response.data as CalendarLoadConfigResult;

                                    specialFieldData.current = {
                                        dataStructureForField: config.data.dataStructure,
                                        columnDefinitions    : []
                                    };
                                }
                            };
                            break;

                        case 'chart':
                            break;

                        case'chartView':
                            break;

                        case 'overview':
                            break;

                        case 'overviewView':
                            loadFnc = async () => {
                                const response = await xhr.post(
                                    `${BASE_URL}/api/overview/loadConfig`,
                                    {
                                        viewKey: specialFieldConfig.viewKey
                                    } as OverviewLoadConfigParameters,
                                    undefined,
                                    uid,
                                    'loadSpecialFieldData'
                                );

                                if ( response?.data ) {
                                    const config = response.data as OverviewLoadConfigResult;

                                    specialFieldData.current = {
                                        dataStructureForField: config.data.dataStructure,
                                        columnDefinitions    : config.data.columnDefinitions,
                                        defaultFilters       : config.data.defaultFilters,
                                        defaultFixFilters    : config.data.fixFilters,
                                        defaultSorters       : config.data.defaultSorters
                                    };
                                }
                            };
                            break;
                    }

                    if ( loadFnc ) {
                        void loadFnc();
                    }
                }
            },
            [
                specialFieldConfig
            ]
        );

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        useEffect(
            () => {
                if ( errors.length ) {
                    if ( useHookForm ) {
                        onIntermediateError( errors );

                        if ( validTimeoutId.current ) {
                            clearTimeout( validTimeoutId.current );
                        }
                        validTimeoutId.current = setTimeout( onError.bind( null, errors ), useHookFormDelay );
                    }
                    else {
                        onError( errors );
                    }
                }
                else {
                    if ( useHookForm ) {
                        onIntermediateValid?.();

                        if ( onValid ) {
                            if ( validTimeoutId.current ) {
                                clearTimeout( validTimeoutId.current );
                            }
                            validTimeoutId.current = setTimeout( onValid, useHookFormDelay );
                        }
                    }
                    else {
                        onValid?.();
                    }
                }
            },
            [
                errors
            ]
        );

        // restore cursor from logical anchor after reformat; set height if autoHeight
        useLayoutEffect(
            () => {
                const editor = document.getElementById( elementId );

                if ( editor && editor.tagName === 'TEXTAREA' ) {
                    const textarea = editor as HTMLTextAreaElement;

                    if ( pendingCursorAnchor.current ) {
                        const { anchor, fallbackOffset, domValueBeforeUpdate } = pendingCursorAnchor.current;
                        pendingCursorAnchor.current = null;

                        // If React did not change the DOM (e.g. JSON.parse failed so no
                        // reformat occurred and the displayed value matches what the user
                        // typed), the browser-native cursor is already correct. Touching it
                        // would only cause unwanted jumps.
                        if ( textarea.value !== domValueBeforeUpdate ) {
                            const resolved = anchor ? resolveAnchor( textarea.value, anchor ) : null;
                            const target = resolved ?? Math.min( fallbackOffset, textarea.value.length );
                            textarea.setSelectionRange( target, target );
                        }
                    }

                    if ( height === 0 ) {
                        if ( textarea.scrollHeight ) {
                            textarea.style.height = '2.2rem';
                            textarea.style.height = `${textarea.scrollHeight + 2}px`;
                        }
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && !specialFieldConfig && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            errorMessage={infoTextLayout !== 'text' ? props.errorMessage : undefined}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {getEditor()}
        </FieldWrapper>;
    }
};
JsonField.defaultProps = defaultProps;

export default JsonField;
